import React, { useState } from 'react'

import { TextLink } from '@octane/spark'
import { DisclosureModal } from 'components/DisclosureModal'
import { ConsentLabel } from 'components/FormikFields/FormikCheckbox/FormikCheckbox.styled'
import config from 'config'
import { useLender } from 'contexts/LenderContextProvider'
import useApplicationDisclosure from 'hooks/useApplicationDisclosures'
import useLenderDisclosure from 'hooks/useLenderDisclosure'
import DisclosureType from 'state/enums/DisclosureType'
import LenderName from 'state/enums/LenderName'

interface ConsentCheckboxLabelProps {
    disclosureType: DisclosureType
    withDisclosureModal?: boolean
    isReadOnly?: boolean
}

export default function ConsentCheckboxLabel({
    disclosureType,
    withDisclosureModal,
    isReadOnly,
}: Readonly<ConsentCheckboxLabelProps>): JSX.Element {
    const [showDisclosureModal, setShowDisclosureModal] = useState(false)
    const { id: lenderId, name: lenderName } = useLender()

    const [showRoadrunnerDisclosure, setShowRoadrunnerDisclosure] =
        useState(false)

    const { disclosureText } = useLenderDisclosure(disclosureType, lenderId)

    const applicationId = Number(
        window.location.href.split('application/').reverse()[0].split('/')[0],
    )

    const { applicationDisclosureText } = useApplicationDisclosure(
        applicationId,
        disclosureType,
    )

    const textPrefix = `By checking this box you have read and agreed to the following: ${' '}`

    const textContent =
        isReadOnly && applicationDisclosureText
            ? applicationDisclosureText
            : (disclosureText ?? '')

    const isHNB = lenderName === LenderName.HUNTINGTON_NATIONAL_BANK

    return (
        <ConsentLabel>
            {withDisclosureModal === true ? (
                <>
                    <DisclosureModal
                        showModal={showDisclosureModal}
                        toggleModal={setShowDisclosureModal}
                        modalTitle={
                            isHNB
                                ? 'Huntington General Application Disclosures'
                                : 'General Application Disclosures'
                        }
                        disclosureName={disclosureType}
                        lenderId={lenderId}
                        applicationId={applicationId}
                        textContent={textContent}
                    />
                    <div
                        className="no-print"
                        style={{
                            display: 'block',
                            paddingLeft: '1vw',
                        }}
                    >
                        {textPrefix}
                        <TextLink onClick={() => setShowDisclosureModal(true)}>
                            {isHNB
                                ? 'Huntington General Application Disclosures'
                                : 'General Application Disclosures'}
                        </TextLink>
                        {isHNB && (
                            <>
                                <span>,</span>
                                <TextLink
                                    onClick={() =>
                                        setShowRoadrunnerDisclosure(true)
                                    }
                                >
                                    Roadrunner General Application Disclosures
                                </TextLink>
                                <DisclosureModal
                                    showModal={showRoadrunnerDisclosure}
                                    toggleModal={setShowRoadrunnerDisclosure}
                                    modalTitle="Roadrunner General Application Disclosures"
                                    disclosureName={disclosureType}
                                    lenderId={config.ROADRUNNER_LENDER_ID}
                                    applicationId={applicationId}
                                />
                            </>
                        )}
                        .
                    </div>
                    <div
                        style={{
                            paddingLeft: '1vw',
                        }}
                        className="print-only"
                    >
                        {textPrefix}
                        <div
                            style={{
                                paddingTop: '20px',
                            }}
                        >
                            {textContent}
                        </div>
                    </div>
                </>
            ) : (
                <div
                    style={{
                        display: 'block',
                        paddingLeft: '1vw',
                        whiteSpace: 'pre-line',
                    }}
                >
                    {`Customer ID Verification Acknowledgment:\n\n${textContent}`}
                </div>
            )}
        </ConsentLabel>
    )
}

ConsentCheckboxLabel.defaultProps = {
    withDisclosureModal: true,
}
